import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, X, Save, Loader2 } from "lucide-react";
import { admin } from "@/lib/api";
import { Field, TabHeader, Badge, inputCls, textareaCls } from "@/admin/ui";

const EMPTY = { quote: "", name: "", role: "", approved: false };

export default function TestimonialsTab() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => admin.list("testimonials").then(setItems).catch(() => toast.error("Could not load testimonials"));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const save = async () => {
    if (!form.quote.trim() || !form.name.trim()) return toast.error("Quote and name are required");
    setSaving(true);
    try {
      if (form.id) await admin.update("testimonials", form.id, form);
      else await admin.create("testimonials", form);
      toast.success("Testimonial saved");
      setForm(null);
      load();
    } catch {
      toast.error("Save failed — please try again");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete testimonial from ${t.name}?`)) return;
    await admin.remove("testimonials", t.id);
    toast.success("Deleted");
    load();
  };

  const toggleApprove = async (t) => {
    await admin.update("testimonials", t.id, { ...t, approved: !t.approved });
    toast.success(t.approved ? "Unpublished" : "Approved — now live on the site");
    load();
  };

  if (form) {
    return (
      <div data-testid="admin-testimonial-form">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl font-semibold text-foreground">
            {form.id ? "Edit Testimonial" : "New Testimonial"}
          </h1>
          <button onClick={() => setForm(null)} className="btn-secondary !px-4 !py-2 !text-xs"><X className="h-3.5 w-3.5" /> Cancel</button>
        </div>
        <div className="mt-8 grid gap-5 rounded-3xl border border-border bg-card p-7 shadow-soft">
          <Field label="Quote"><textarea className={textareaCls} rows={4} value={form.quote} onChange={set("quote")} data-testid="testimonial-quote-input" /></Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Name"><input className={inputCls} value={form.name} onChange={set("name")} data-testid="testimonial-name-input" /></Field>
            <Field label="Role" hint='e.g. "Parent of two"'><input className={inputCls} value={form.role} onChange={set("role")} data-testid="testimonial-role-input" /></Field>
          </div>
          <label className="flex items-center gap-2.5 text-sm text-foreground">
            <input type="checkbox" checked={form.approved} onChange={(e) => setForm((f) => ({ ...f, approved: e.target.checked }))} data-testid="testimonial-approved-checkbox" className="h-4 w-4 accent-terracotta-500" />
            Approved — show on the website
          </label>
        </div>
        <button onClick={save} disabled={saving} data-testid="admin-testimonial-save" className="btn-primary mt-6 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? "Saving…" : "Save Testimonial"}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="admin-testimonials-tab">
      <TabHeader
        title="Testimonials"
        description="Approve and publish client reviews. Nothing appears on the site until you approve it."
        actionLabel="New Testimonial"
        onAdd={() => setForm({ ...EMPTY })}
        testId="admin-add-testimonial"
      />
      <div className="mt-8 space-y-3">
        {(items || []).map((t) => (
          <div key={t.id} data-testid={`admin-testimonial-row-${t.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4 shadow-soft">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">“{t.quote}”</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{t.name}{t.role ? ` · ${t.role}` : ""}</p>
            </div>
            <button onClick={() => toggleApprove(t)} data-testid={`admin-testimonial-approve-${t.id}`}>
              <Badge tone={t.approved ? "forest" : "muted"}>{t.approved ? "Live" : "Pending"}</Badge>
            </button>
            <button onClick={() => setForm({ ...t })} data-testid={`admin-testimonial-edit-${t.id}`} aria-label="Edit testimonial" className="rounded-full border border-border p-2 text-muted-foreground transition-colors duration-300 hover:border-terracotta-500 hover:text-terracotta-500">
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => remove(t)} data-testid={`admin-testimonial-delete-${t.id}`} aria-label="Delete testimonial" className="rounded-full border border-border p-2 text-muted-foreground transition-colors duration-300 hover:border-destructive hover:text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {items && items.length === 0 && (
          <p className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            No testimonials yet. The homepage currently shows a "Testimonials Coming Soon" card.
          </p>
        )}
      </div>
    </div>
  );
}
